import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchParam } from 'react-use';
import { useAccount, useDisconnect, useWalletClient } from 'wagmi';

import { useWalletSelector } from '@/context/WalletSelectorContext';
import useChain from '@/hooks/useChain';
import { toShortAddress } from '@/utils/address';
import { resetWagmiState } from '@/utils/wagmi';
import { signOut } from '@/utils/wallet';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { useWeb3Modal } from '@web3modal/wagmi/react';

export function useChainSignInOut() {
  const modal = useSearchParam('modal');
  const navigate = useNavigate();

  const chain = useChain();
  const solanaWalletModal = useWalletModal();
  const solanaWallet = useWallet();
  const { modal: nearWalletModal, accountId: nearAccountId } = useWalletSelector();
  const { data: walletClient } = useWalletClient();
  const { disconnect } = useDisconnect();
  const ethAccount = useAccount();
  const { open: ethModalOpen } = useWeb3Modal();

  const ChainIsSignedIn = chain?.isSignedIn && chain.chainId;

  const accountAddress: string = useMemo(() => {
    if (!chain) return '';

    // near
    if (chain.chainId === 'near') {
      return nearAccountId || '';
    }

    // solana
    if (chain.chainId === 'solana') {
      if (!solanaWallet.publicKey) return '';
      return solanaWallet.publicKey.toBase58();
    }

    // eth
    if (chain.chainId === 'eth') {
      return ethAccount.address || '';
    }

    return '';
  }, [chain, nearAccountId, solanaWallet.publicKey, ethAccount]);

  const shortAccountAddress = useMemo(() => {
    return toShortAddress(accountAddress);
  }, [accountAddress]);

  const onSignIn = useCallback(() => {
    if (!chain) return;

    // near
    if (chain.chainId === 'near') {
      nearWalletModal.show();
      return;
    }

    // solana
    if (chain.chainId === 'solana') {
      solanaWalletModal.setVisible(true);
      return;
    }

    // eth
    if (chain.chainId === 'eth') {
      if (!walletClient && ethAccount.isConnected) {
        // reset wagmi state
        resetWagmiState();
        return;
      }
      ethModalOpen();
    }
  }, [chain, nearWalletModal, solanaWalletModal, walletClient, ethAccount, ethModalOpen]);

  const onSignOut = useCallback(async () => {
    if (!chain) return;
    if (!chain.isSignedIn) return;

    // near
    if (chain.chainId === 'near') {
      if (!nearAccountId) return;
      await signOut();
      return;
    }

    // solana
    if (chain.chainId === 'solana') {
      if (!solanaWallet.publicKey) return;
      await solanaWallet.disconnect();
      return;
    }

    // eth
    if (chain.chainId === 'eth') {
      if (!ethAccount.address) return;
      await disconnect();
    }
  }, [chain, nearAccountId, solanaWallet, ethAccount, disconnect]);

  useEffect(() => {
    if (modal === 'eth-connect') {
      ethModalOpen();
      const url = new URL(window.location.href);
      url.searchParams.delete('modal');
      navigate(`${url.pathname}${url.search}`);
    }
  }, [modal, ethModalOpen, navigate]);

  return {
    chain,
    accountAddress,
    shortAccountAddress,
    ChainIsSignedIn,
    onSignIn,
    onSignOut,
  };
}
